// @ts-strict-ignore
import type { EmersonProductSearchResultOpen } from "@clinic/emerson/data/splinters/EmersonProductSearchResultOpen.fragment.generated";
import { EmersonProductCard } from "@emerson/components/EmersonProductCard/EmersonProductCard";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import type { EmersonProductSearchResult } from "@emerson/data/splinters/EmersonProductSearchResult.fragment.generated";
import { useEmersonCatalogParams } from "@emerson/hooks/useEmersonCatalogParams/useEmersonCatalogParams";
import { ProductResults } from "@shared/components/ProductResults/ProductResults";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { EmersonViewProductEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useCatalogSearchResultTitle } from "@shared/hooks/useCatalogSearchResultTitle/useCatalogSearchResultTitle";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { isE2E } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { ProductBanner } from "../ProductBanner/ProductBanner";
import { useWordpressSearchLandingPageBySearchTermQuery } from "../queries/WordpressSearchLandingPageBySearchTermQuery/WordpressSearchLandingPageBySearchTermQuery.query";
import { useWordpressSearchLandingPageQuery } from "../queries/WordpressSearchLandingPageQuery/WordpressSearchLandingPageQuery.query";

import * as styles from "./CatalogResults.styles";

interface Props {
  searchResults: EmersonProductSearchResult[] | EmersonProductSearchResultOpen[];
  totalCount: number;
  handleClearFilters: () => void;
  handleLoadMore?: () => void;
  isLoadingNewResults: boolean;
  isLoadingMoreResults: boolean;
  pinnedIds: string[];
}

const CatalogResults = ({
  searchResults,
  pinnedIds,
  totalCount,
  handleClearFilters,
  handleLoadMore,
  isLoadingNewResults,
  isLoadingMoreResults,
}: Props) => {
  const { isUnauthenticated } = useSharedGlobalConfig();

  const resultCount = totalCount ?? 0;
  const displayedCount = searchResults?.length ?? 0;

  const [catalogParams] = useEmersonCatalogParams();
  const category = catalogParams?.taxonomies?.pop().name;
  // const attribute = catalogParams?.taxonomies?.pop().name;
  const brand = catalogParams.brands?.pop().name;
  const searchterms = catalogParams?.query || null;

  const hasSearchQuery = searchterms;
  const hasFiltersQuery = (category || brand) && !hasSearchQuery;

  // limited by wordpress graphql query
  const { loading: loadingSearchTermBanner, data: searchQueryData } =
    useWordpressSearchLandingPageBySearchTermQuery({
      variables: {
        searchterms,
      },
      skip: !hasSearchQuery || isE2E(),
    });

  const { loading: loadingBanner, data: taxonomiesQueryData } = useWordpressSearchLandingPageQuery({
    variables: {
      category,
      brand,
    },
    skip: !hasFiltersQuery || isE2E(),
  });

  const loadingBannerData = loadingSearchTermBanner || loadingBanner;
  const banner =
    searchQueryData?.searchLandingPages?.nodes[0] ||
    taxonomiesQueryData?.searchLandingPages?.nodes[0];

  const renderProductCards = () => {
    return searchResults?.map((product, index) => {
      const searchRank = index + 1;

      return (
        <EmersonProductCard
          key={product?.id}
          entryPoint={EmersonViewProductEntryPoint.PRODUCT_CARD}
          variant={product.availableOrMasterVariant}
          product={product}
          isFeatured={pinnedIds.includes(product.id)}
          brandName={product?.brand?.name}
          searchRank={searchRank}
          to={emersonRoutes.getPDP(
            product?.brand?.name,
            product?.slug,
            product?.id,
            product?.availableOrMasterVariant?.emersonVariantId
          )}
        />
      );
    });
  };

  return (
    <>
      <ProductBanner isLoading={loadingBannerData} banner={banner} />
      <ProductResults
        title={useCatalogSearchResultTitle("", displayedCount, resultCount)}
        resultCount={resultCount}
        displayedCount={displayedCount}
        onLoadMore={handleLoadMore}
        isLoadingNewResults={isLoadingNewResults}
        isLoadingMoreResults={isLoadingMoreResults}
        onClearFilters={handleClearFilters}
        eventNameForLogging={
          isUnauthenticated
            ? AnalyticsEvent.EMERSON_UNAUTH_SEARCH_APPLY_FILTER
            : AnalyticsEvent.EMERSON_SEARCH_APPLY_FILTER
        }
        entryPointForLogging={SearchApplyEntryPoint.CATALOG_PAGE}
        stickyHeaderStyle={styles.stickyHeader}
      >
        {renderProductCards()}
      </ProductResults>
    </>
  );
};

export { CatalogResults };
